import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import commentary from "../../assets/images/commentary.png";
import MatchCard from "../common/MatchCard";
import { fetchMatchesAsync } from "../../store/slices/matchesSlice";
import { getSeriesInfoBySportsName } from "../../store/slices/sidebarSlice";
import cricket from "../../assets/images/menu-4.png";
import football from "../../assets/images/menu-1.png";
import tennis from "../../assets/images/menu-2.png";
import casino from "../../assets/images/menu-99998.png";
import rugby from "../../assets/images/menu-5.png";
import tableTennis from "../../assets/images/menu-20.png";
import futsal from "../../assets/images/menu-29.png";
import darts from "../../assets/images/menu-3503.png";
import baseball from "../../assets/images/menu-7511.png";
import basketball from "../../assets/images/menu-7522.png";
import iceHockey from "../../assets/images/menu-7524.png";
import binary from "../../assets/images/menu-99990.png";
import volleyball from "../../assets/images/menu-998917.png";
import mixedMartialArts from "../../assets/images/menu-26420387.png";
import politics from "../../assets/images/menu-2378961.png";
import horseRacing from "../../assets/images/menu-7.png";
import greyhoundRacing from "../../assets/images/menu-4339.png";
import {
  sportWiseMatchesFecthInterval,
  horseAndGreyhoundMatchesFecthInterval,
} from "../../env";
import { isAuthenticated } from "../../utils/auth";
import {
  CASINO_PROVIDER_IMAGES,
  THUMB_IMAGES,
} from "../../constants/casinoProviderImages";
import {
  navigateToCasinoGameLaunch,
  thumbItemHasPath,
} from "../../utils/casinoLaunchNavigation";

// All event_type_id values with title and icon
const SPORT_CONFIG = {
  1: { title: "Football", icon: football },
  2: { title: "Tennis", icon: tennis },
  4: { title: "Cricket", icon: cricket },
  5: { title: "Rugby", icon: rugby },
  7: { title: "Horse Racing", icon: horseRacing },
  20: { title: "Table Tennis", icon: tableTennis },
  29: { title: "Futsal", icon: futsal },
  3503: { title: "Darts", icon: darts },
  4339: { title: "Greyhound Racing", icon: greyhoundRacing },
  7511: { title: "Baseball", icon: baseball },
  7522: { title: "Basketball", icon: basketball },
  7524: { title: "Ice Hockey", icon: iceHockey },
  99990: { title: "Binary", icon: binary },
  99991: { title: "Sports book", icon: casino },
  99994: { title: "Kabaddi", icon: cricket },
  998917: { title: "Volleyball", icon: volleyball },
  26420387: { title: "Mixed Martial Arts", icon: mixedMartialArts },
  2378961: { title: "Politics", icon: politics },
};

// Sport IDs that use the Racing layout/structure instead of the default sports structure
const RACING_SPORT_IDS = [7, 4339];

// Logged-out home matches may omit `venue`; align with Sidebar racing grouping.
const getMatchVenueForRacing = (m) =>
  m?.venue ||
  m?.race_venue ||
  m?.venue_name ||
  m?.track_name ||
  m?.track ||
  m?.racecourse ||
  m?.meeting_name ||
  m?.region ||
  m?.city ||
  m?.name ||
  m?.match_name ||
  m?.event_name ||
  m?.event ||
  "Unknown";

const getRacingTimeIso = (m) =>
  m?.market_start_time || m?.match_date || m?.open_date || null;

// Logged-out racing can omit `country_code`; try to derive from venue text like "GREYVILLE (RSA)".
const getRacingCountryCode = (m) => {
  const rawCode = m?.country_code ?? m?.countryCode;
  if (rawCode != null && rawCode !== "") return String(rawCode).toUpperCase();

  const venue = getMatchVenueForRacing(m);
  if (typeof venue !== "string" || !venue) return "";
  // Venue strings can be like: "Horsham (AUS) 31st Mar (1 March)" → pick first "(AUS)".
  const match = venue.match(/\(([A-Za-z]{2,4})\)/);
  return match?.[1] ? String(match[1]).toUpperCase() : "";
};

// Format ISO market_start_time to local time string e.g. "05:50"
const formatMarketTime = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  } catch {
    return "";
  }
};

const Sports = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const auth = useSelector((state) => state.auth || {});
  const authToken = auth?.token;
  const loggedIn = Boolean(authToken) || isAuthenticated();
  const { matchesBySport, loading } = useSelector((state) => state.matches);
  const { news } = useSelector((state) => state.news);
  const racingMarketsRaw = useSelector((state) =>
    id ? state.sidebar.seriesBySport[String(id)] : null,
  );
  const sidebarLoading = useSelector((state) => state.sidebar.loading);
  const [activeRacingTab, setActiveRacingTab] = useState(0);
  const eventTypeId = id ? parseInt(id, 10) : null;
  const sportConfig = eventTypeId != null ? SPORT_CONFIG[eventTypeId] : null;
  const title = sportConfig?.title ?? "Sport";
  const icon = sportConfig?.icon ?? cricket;

  const newsHeadingsText = useMemo(() => {
    const payload = news;
    const items = Array.isArray(payload)
      ? payload
      : Array.isArray(payload?.data)
        ? payload.data
        : [];

    return items
      .map((x) => x?.heading)
      .filter(Boolean)
      .join("  •  ");
  }, [news]);

  const handleGameClick = useCallback(
    (game) => {
      navigateToCasinoGameLaunch(navigate, game, {}, auth);
    },
    [navigate, auth],
  );

  useEffect(() => {
    if (eventTypeId == null || Number.isNaN(eventTypeId)) return;
    // Logged-in racing: lists come from allRacingMarkets, not homeMatches per sport
    if (RACING_SPORT_IDS.includes(eventTypeId) && loggedIn) return;

    // Initial fetch
    dispatch(fetchMatchesAsync(eventTypeId));

    // Short-pool: refresh matches every 4 seconds
    const intervalId = setInterval(() => {
      dispatch(fetchMatchesAsync(eventTypeId));
    }, sportWiseMatchesFecthInterval);

    // Cleanup on unmount or when sport changes
    return () => clearInterval(intervalId);
  }, [dispatch, eventTypeId, loggedIn]);

  // Load racing markets for Horse (7) and Greyhound (4339) — logged-in only
  useEffect(() => {
    if (
      eventTypeId != null &&
      !isNaN(eventTypeId) &&
      RACING_SPORT_IDS.includes(eventTypeId) &&
      loggedIn
    ) {
      const fetchRacingMarkets = () =>
        dispatch(
          getSeriesInfoBySportsName({
            sportId: eventTypeId,
            isRacingMarket: true,
          }),
        );

      // Initial fetch
      fetchRacingMarkets();

      // Short-pool: refresh racing markets
      const intervalId = setInterval(() => {
        fetchRacingMarkets();
      }, horseAndGreyhoundMatchesFecthInterval);

      return () => clearInterval(intervalId);
    }
  }, [dispatch, eventTypeId, loggedIn]);

  const matches =
    eventTypeId != null && !isNaN(eventTypeId)
      ? matchesBySport[String(eventTypeId)] || []
      : [];

  const isRacingLayout =
    eventTypeId != null && RACING_SPORT_IDS.includes(eventTypeId);

  const racingMarketsList = useMemo(() => {
    if (!isRacingLayout) return [];
    // if (!racingMarketsRaw) return [];

    // allRacingMarkets can arrive either directly as array or wrapped under data.
    const source = Array.isArray(racingMarketsRaw)
      ? racingMarketsRaw
      : Array.isArray(racingMarketsRaw?.data)
        ? racingMarketsRaw.data
        : [];

    if (Array.isArray(source)) return source;
    return [];
  }, [isRacingLayout, loggedIn, racingMarketsRaw]);

  const racingRegionTabs = useMemo(() => {
    if (!isRacingLayout) return [];
    const tabSource = loggedIn ? racingMarketsList : matches;
    if (!Array.isArray(tabSource)) return [];

    const seen = new Set();
    const tabs = [];

    tabSource.forEach((m) => {
      const code = loggedIn ? getRacingCountryCode(m) : getRacingCountryCode(m);
      if (!code) return;
      if (!seen.has(code)) {
        seen.add(code);
        tabs.push(code);
      }
    });

    return tabs;
  }, [isRacingLayout, loggedIn, racingMarketsList, matches]);

  const selectedRacingRegion = racingRegionTabs[activeRacingTab];

  // Reset selected racing region when switching between racing sports.
  useEffect(() => {
    if (!isRacingLayout) return;
    setActiveRacingTab(0);
  }, [eventTypeId, isRacingLayout]);

  // Keep active tab index valid when dynamic tab list updates.
  useEffect(() => {
    if (!isRacingLayout) return;
    if (racingRegionTabs.length === 0) {
      setActiveRacingTab(0);
      return;
    }
    if (activeRacingTab >= racingRegionTabs.length) {
      setActiveRacingTab(0);
    }
  }, [isRacingLayout, racingRegionTabs, activeRacingTab]);

  // Normalize racing markets to array, filter by selected region tab, group by venue
  const racingByRegionAndVenue = useMemo(() => {
    if (!isRacingLayout) return [];

    let list;
    if (loggedIn) {
      list = racingMarketsList;
    } else {
      list = (matches || []).map((m) => ({
        ...m,
        venue: getMatchVenueForRacing(m),
        market_start_time: getRacingTimeIso(m),
        country_code: getRacingCountryCode(m) || m?.country_code,
        match_id: m?.match_id ?? m?.event_id,
        market_id: m?.market_id ?? m?.marketId,
      }));
    }

    if (!loggedIn && !Array.isArray(list) && list && typeof list === "object") {
      list = Object.values(list);
    }
    if (!Array.isArray(list)) return [];

    const region = selectedRacingRegion;
    if (!region) return {};
    const markets = loggedIn
      ? list.filter((m) => m && m.country_code != null && m.country_code !== "")
      : list;

    const byRegion = markets.filter((m) => {
      const cc = m?.country_code ?? m?.countryCode;
      if (cc == null || cc === "") {
        // If region is missing, keep it under the first available tab.
        return activeRacingTab === 0;
      }
      return String(cc).toUpperCase() === String(region).toUpperCase();
    });

    const byVenue = {};
    byRegion.forEach((m) => {
      const v = m.venue || "Unknown";
      if (!byVenue[v]) byVenue[v] = [];
      byVenue[v].push(m);
    });
    return byVenue;
  }, [
    isRacingLayout,
    loggedIn,
    racingMarketsList,
    matches,
    activeRacingTab,
    selectedRacingRegion,
  ]);

  const hasRacingDataLoggedIn =
    loggedIn &&
    Array.isArray(racingMarketsList) &&
    racingMarketsList.length > 0;

  const hasRacingDataLoggedOut =
    !loggedIn && Array.isArray(matches) && matches.length > 0;

  const racingLoading =
    isRacingLayout && loggedIn
      ? sidebarLoading && !hasRacingDataLoggedIn
      : isRacingLayout && !loggedIn
        ? loading && !hasRacingDataLoggedOut
        : false;

  if (isRacingLayout) {
    return (
      <section className="section listing_page">
        <div className="row">
          <div className="col-12 d-flex">
            <div className="commentary">
              <img src={commentary} alt="commentary" />
              <div className="commentary-ticker" aria-live="polite">
                <span>{newsHeadingsText || "Welcome..."}</span>
              </div>
            </div>
          </div>
        </div>
        <div className="col-lg-12 listing_page_12">
          <div className="middle-page-content home-page">
            <div className="game-slider mb-2">
              <div className="coupon-card">
                <div className="card">
                  <div className="card-body card-content p-0">
                    <div className="bet-table-header">
                      <div className="row align-items-center g-0">
                        <div className="col-md-4">
                          <span className="list-sport-title">
                            <img
                              className="img-fluid game-icon-img"
                              src={icon}
                              alt={title}
                            />
                            {title}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="bet-table-body fancy-tab">
                      <div className="tab-container">
                        <ul
                          role="tablist"
                          className="nav nav-tab fancy-subtabs"
                          aria-label="Tabs"
                        >
                          {racingRegionTabs.map((code, index) => (
                            <li
                              key={code}
                              className={`nav-item${activeRacingTab === index ? " active" : ""}`}
                            >
                              <Link
                                to="#"
                                role="tab"
                                className={`nav-link${activeRacingTab === index ? " active" : ""}`}
                                aria-controls={`racing-tab-${index}`}
                                aria-selected={activeRacingTab === index}
                                id={`racing-tab-${index}-link`}
                                onClick={() => setActiveRacingTab(index)}
                              >
                                <span>{code}</span>
                              </Link>
                            </li>
                          ))}
                        </ul>
                        <div className="tab-content">
                          <div
                            role="tabpanel"
                            aria-labelledby={`racing-tab-${activeRacingTab}-link`}
                            className="tab-pane active"
                            id={`racing-tab-${activeRacingTab}`}
                          >
                            {racingLoading ? (
                              <div className="bet-table-row">
                                <div className="row">
                                  <div className="col-12 py-3 text-center">
                                    Loading…
                                  </div>
                                </div>
                              </div>
                            ) : Object.keys(racingByRegionAndVenue).length ===
                              0 ? (
                              <div className="bet-table-row">
                                <div className="row">
                                  <div className="col-12 py-3 text-center">
                                    No race available
                                  </div>
                                </div>
                              </div>
                            ) : (
                              Object.entries(racingByRegionAndVenue).map(
                                ([venue, markets], meetingIndex) => (
                                  <div
                                    className="bet-table-row"
                                    key={`${venue}-${meetingIndex}`}
                                  >
                                    <div className="row">
                                      <div className="col-md-3 col-12">
                                        <div className="game-box">
                                          <div className="game-left-col">
                                            <div className="game-name">
                                              <a
                                                href="#"
                                                className="text-decoration-none text-dark"
                                                onClick={(e) =>
                                                  e.preventDefault()
                                                }
                                              >
                                                <p className="team-name text-left">
                                                  {venue} (
                                                  {selectedRacingRegion || ""}{" "}
                                                  {markets[0]?.market_start_time
                                                    ? new Date(
                                                        markets[0]
                                                          .market_start_time,
                                                      ).toLocaleDateString([], {
                                                        day: "numeric",
                                                        month: "short",
                                                      })
                                                    : ""}
                                                  )
                                                </p>
                                                <p className="team-name text-left team-event" />
                                              </a>
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                      <div className="col-md-9 col-12 text-left">
                                        <div className="racing_listing">
                                          {markets.map((m, timeIndex) => (
                                            <Link
                                              key={m.match_id || timeIndex}
                                              to={`/sports/detail/${eventTypeId}/${m.match_id || timeIndex}?marketId=${encodeURIComponent(
                                                m.market_id || m.marketId || "",
                                              )}&marketStartTime=${encodeURIComponent(
                                                m.market_start_time || "",
                                              )}`}
                                              className="rl_div"
                                              onClick={(e) => {
                                                if (loggedIn) return;
                                                e.preventDefault();
                                                if (
                                                  typeof window !==
                                                    "undefined" &&
                                                  typeof window.openLoginDialog ===
                                                    "function"
                                                ) {
                                                  window.openLoginDialog();
                                                }
                                                toast.error(
                                                  "Please login to continue.",
                                                );
                                              }}
                                            >
                                              <span className="d-block bet-button-price">
                                                {formatMarketTime(
                                                  m.market_start_time,
                                                ) ||
                                                  m.name ||
                                                  "—"}
                                              </span>
                                            </Link>
                                          ))}
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                ),
                              )
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="section listing_page">
      <div className="row">
        <div className="col-12 d-flex">
          <div className="commentary">
            <img src={commentary} alt="commentary" />
            <div className="commentary-ticker" aria-live="polite">
              <span>{newsHeadingsText || "Welcome..."}</span>
            </div>
          </div>
        </div>
      </div>
      <div className="col-lg-12 listing_page_12">
        <div className="middle-page-content home-page">
          <div className="game-slider mb-2">
            <div className="coupon-card">
              <div className="custom-thumb-section">
                {THUMB_IMAGES.map((item, index) => (
                  <div
                    key={item.path || item.game_id || item.game_code || index}
                    className={`cts-img popularDiv${item.hcDiv ? " hc_div" : ""}`}
                  >
                    {thumbItemHasPath(item) ? (
                      <Link to={item.path}>
                        <img
                          className="img-fluid"
                          src={item.src}
                          alt={item.game_name || ""}
                        />
                      </Link>
                    ) : (
                      <div
                        role="button"
                        tabIndex={0}
                        onClick={() => handleGameClick(item)}
                        onKeyDown={(e) =>
                          e.key === "Enter" && handleGameClick(item)
                        }
                        style={{ cursor: "pointer" }}
                      >
                        <img
                          className="img-fluid"
                          src={item.src}
                          alt={item.game_name || ""}
                        />
                      </div>
                    )}
                  </div>
                ))}
              </div>
              <div className="card">
                <div className="card-body card-content p-0">
                  <MatchCard
                    sportTitle={title}
                    matches={matches}
                    sportIcon={icon}
                    instanceId="today"
                  />
                </div>
              </div>
            </div>
            <span className="list-sport-title">
              <img
                src={casino}
                className="img-fluid game-icon-img"
                alt="casino"
              />{" "}
              Casino Provider
            </span>
            <div className="casinoprovider-thumb-section">
              {CASINO_PROVIDER_IMAGES.map((item, index) => (
                <div key={index} className="cts-img popularDiv">
                  <Link to={item.path || "/casino"}>
                    <img className="img-fluid" alt="" src={item.src} />
                  </Link>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Sports;
